import { useEffect, useState } from "react";

/**
 * Shared mechanics for a page that holds its data sections back so they can
 * appear together, instead of popping in one by one as each request lands.
 *
 * Home was the first page to do this - @see `useHomeReveal` - and venue
 * details is the second - @see the "Coordinated reveal" block in its route.
 * The timings, the fade and the cap live here so the two pages feel the same.
 */

/**
 * The longest a page holds its data sections back. Past this, whatever has
 * loaded is shown and the rest keep their own skeletons - one slow request
 * must not hold the whole page hostage.
 */
export const PAGE_REVEAL_CAP_MS = 3000;

/** How long each band of the page takes to settle in. */
export const PAGE_REVEAL_DURATION_MS = 700;

/** The gap between one band starting to come in and the next. */
export const PAGE_REVEAL_STAGGER_MS = 80;

/** From the first band starting to the last one settling. */
export const pageRevealTotalMs = (bands: number) =>
  PAGE_REVEAL_DURATION_MS + (bands - 1) * PAGE_REVEAL_STAGGER_MS;

/**
 * One band of the page coming in: a fade with a short rise, on a long
 * ease-out so it arrives quickly and settles slowly. Opacity and a 12px lift
 * only, and nothing at all under reduced motion. Pair with `revealBandStyle`
 * for its place in the cascade.
 *
 * `fill-mode-backwards`, not `both`: it holds the band hidden through its
 * delay, but lets go once it has finished. `both` leaves an identity transform
 * behind, which gives every band its own stacking context - and on home the
 * search field's suggestions, in the second band, would then draw underneath
 * the Contribute band below it.
 *
 * The duration here must match `PAGE_REVEAL_DURATION_MS`. The curve is an
 * arbitrary property rather than `ease-[…]`: that class is claimed by both core
 * Tailwind (transitions) and tailwindcss-animate (animations), and Tailwind
 * generates nothing for an ambiguous arbitrary value.
 */
export const PAGE_REVEAL_FADE =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:duration-700 motion-safe:[animation-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-safe:fill-mode-backwards";

/**
 * Where a band sits in the cascade: 0 for the top of the page. A style rather
 * than a `delay-[…]` class, which is as ambiguous between core Tailwind and
 * tailwindcss-animate as `ease-[…]`.
 */
export const revealBandStyle = (band: number) => ({
  animationDelay: `${band * PAGE_REVEAL_STAGGER_MS}ms`,
});

/**
 * The skeleton going out, over the page coming in, so the two cross-fade
 * rather than the skeleton vanishing and the page appearing on an empty
 * background. Shorter than the cascade, so the skeleton is gone while the
 * lower bands are still settling. Not rendered at all under reduced motion.
 */
export const PAGE_SKELETON_EXIT =
  "motion-reduce:hidden animate-out fade-out duration-[400ms] ease-out fill-mode-forwards";

/**
 * True for `ms` after `active` turns true - long enough for something leaving
 * to finish its exit before it is unmounted.
 */
export function useLingering(active: boolean, ms: number): boolean {
  const [isLingering, setIsLingering] = useState(false);
  const [wasActive, setWasActive] = useState(active);

  // On the render where `active` changes, the state below has not caught up
  // yet, so the answer is worked out from `active` directly - otherwise the
  // first render after turning on would briefly say "not lingering".
  const hasChanged = active !== wasActive;
  if (hasChanged) {
    setWasActive(active);
    setIsLingering(active);
  }

  useEffect(() => {
    if (!isLingering) return;
    const timer = window.setTimeout(() => setIsLingering(false), ms);
    return () => window.clearTimeout(timer);
  }, [isLingering, ms]);

  return hasChanged ? active : isLingering;
}

type PageRevealOptions = {
  /** How long the page may hold its sections back. @see `PAGE_REVEAL_CAP_MS` */
  capMs?: number;
};

/**
 * Coordinates a page's data sections so they swap their skeletons for content
 * in the same frame.
 *
 * Returns whether the sections may show their data. Until then every section
 * is told it is loading, whether or not its own request has finished.
 *
 * - **Opens** when nothing is pending any more, or `capMs` after it was armed,
 *   whichever comes first.
 * - **Re-arms** when `key` changes to a different non-null value, since every
 *   section is about to be replaced. Not on the first key arriving - that is
 *   still the first load, and the cap keeps counting from the first paint -
 *   and not on a background refetch, which updates in place.
 *
 * Only data waits on this. Controls that need nothing from the network stay
 * usable from the first paint.
 *
 * @param isPending any data section still waiting on its first answer for this
 * key - including "no key yet", when the queries have not even started.
 * @param key what the page is showing (home: the coordinates; venue details:
 * the slug), or `null` before it has one.
 */
export function usePageReveal(
  isPending: boolean,
  key: string | null,
  { capMs = PAGE_REVEAL_CAP_MS }: PageRevealOptions = {},
): boolean {
  const [isRevealed, setIsRevealed] = useState(false);
  const [armedKey, setArmedKey] = useState(key);

  // Adjusted during render rather than in an effect, so a key change puts the
  // sections back behind their skeletons before the new key's first frame is
  // painted, not one frame after.
  if (armedKey !== key) {
    setArmedKey(key);
    if (armedKey !== null && key !== null) {
      setIsRevealed(false);
    }
  }

  useEffect(() => {
    if (isRevealed) return;
    if (!isPending) {
      setIsRevealed(true);
      return;
    }
    const timer = window.setTimeout(() => setIsRevealed(true), capMs);
    return () => window.clearTimeout(timer);
  }, [isPending, isRevealed, capMs]);

  return isRevealed;
}

/**
 * Whether `src` has finished loading - or failed, which counts as done: a
 * broken picture is no reason to hold the page back. A page with no picture to
 * wait for (no `src` yet, or none at all) is never held back either.
 */
export function useImageLoaded(src?: string | null): boolean {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!src) return;
    let isCancelled = false;
    const done = () => {
      if (!isCancelled) setIsLoaded(true);
    };
    const image = new Image();
    image.onload = done;
    image.onerror = done;
    image.src = src;
    // Already in the cache - e.g. the preload got there first.
    if (image.complete) done();
    return () => {
      isCancelled = true;
    };
  }, [src]);

  return src ? isLoaded : true;
}
